import React, { useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { formatPrice } from '../lib/currency';
import { api } from '../lib/api';
import { Sparkles, ArrowRight, Info, TrendingDown, Check, Clock, Luggage, AlertTriangle, MapPin } from 'lucide-react';

const BADGE_CLS = {
  Cheapest: 'bg-emerald-600 text-white', 'Best value': 'bg-accent text-accent-foreground',
  Fastest: 'bg-blue-600 text-white', 'More baggage': 'bg-violet-600 text-white',
  'Hidden-city': 'bg-amber-500 text-white',
};

const fmtDur = (m) => (m ? `${Math.floor(m / 60)}h ${m % 60}m` : '—');

// LIVE Smart Fare card — all values come from the real offer + baseline comparison.

const WARNING_TEXT = {
  INTERMEDIATE_DESTINATION: 'This itinerary is ticketed to a further final city — the traveller exits at the intended stop. Review airline policy and checked-baggage implications before approving.',
  SELF_TRANSFER: 'Self-transfer between separate tickets. The connection is not protected by the airline — verify the layover buffer and re-check-in requirements.',
  ALTERNATE_AIRPORT: 'Uses a nearby alternate airport. Confirm ground transfer time and cost for the traveller.',
};

export const SmartFareWarning = ({ type }) => {
  const text = WARNING_TEXT[type];
  if (!text) return null;
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2" data-testid="smart-fare-warning">
      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
      <p className="text-xs text-amber-800">{text}</p>
    </div>
  );
};

export const SmartFareCard = ({ o, currency, sessionId, onDetail }) => {
  const positive = o.potential_saving > 0;
  const hidden = o.type === 'INTERMEDIATE_DESTINATION';
  return (
    <Card className={`rounded-2xl p-4 ${hidden ? 'border-amber-400/50' : 'border-accent/30'}`} data-testid={`smart-fare-card-${o.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${hidden ? 'text-amber-700 bg-amber-500/10' : 'text-accent bg-accent/10'}`}>
            {hidden ? <MapPin className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />} {hidden ? 'HIDDEN-CITY FARE' : 'SMART FARE'}
          </span>
          <p className="mt-2 font-semibold">{o.origin} → {o.requested_destination}</p>
          {hidden ? (
            <p className="text-xs text-amber-700 mt-0.5" data-testid="hidden-city-route">Ticketed to {o.ticketed_final_city_name || o.ticketed_final_city} ({o.ticketed_final_city}) · exit at {o.exit_city_name || o.requested_destination}</p>
          ) : (
            <p className="text-xs text-muted-foreground mt-0.5">{o.airline_name} {o.flight_number ? `· ${o.flight_number}` : ''} · {o.stops === 0 ? 'Non-stop' : `${o.stops} stop${o.stops === 1 ? '' : 's'}`} · {fmtDur(o.duration_minutes)}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className={`text-2xl font-semibold tabular ${hidden ? 'text-amber-600' : 'text-accent'}`}>{formatPrice(o.smart_price, currency)}</p>
          {positive && <p className="text-xs text-muted-foreground line-through">{formatPrice(o.normal_price, currency)}</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {(o.badges || []).map((b) => <Badge key={b} className={`rounded-full text-[11px] ${BADGE_CLS[b] || 'bg-accent text-accent-foreground'}`}>{b}</Badge>)}
        {positive && <Badge className="rounded-full bg-emerald-500/15 text-emerald-700 text-[11px]"><TrendingDown className="h-3 w-3 mr-1" />Save {formatPrice(o.potential_saving, currency)} · {o.saving_percentage}%</Badge>}
        <Badge variant="outline" className="rounded-full text-[11px]">Score {o.smart_fare_score}/100</Badge>
      </div>
      <div className="mt-3 rounded-xl bg-accent/5 border border-accent/15 p-2.5">
        <p className="text-[11px] font-semibold text-muted-foreground mb-1">Why this is smart</p>
        <ul className="space-y-0.5">
          {(o.reasons || []).slice(0, 5).map((r, i) => (
            <li key={i} className="text-xs text-foreground/90 flex items-center gap-1.5"><Check className="h-3 w-3 text-accent shrink-0" />{r}</li>
          ))}
        </ul>
      </div>
      <Button onClick={() => onDetail(o)} variant="outline" className="w-full rounded-xl mt-3 hover:border-accent" data-testid={`smart-fare-view-${o.id}`}>View details <ArrowRight className="h-4 w-4 ml-1" /></Button>
      {hidden && o.warning && (
        <p className="mt-2 text-[11px] text-amber-700 flex items-start gap-1" data-testid="hidden-city-warning-snippet"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> Carry-on only. You skip the last leg to {o.ticketed_final_city}. Tap details for how it works.</p>
      )}
    </Card>
  );
};

const Field = ({ label, value }) => (
  <div><p className="text-[11px] text-muted-foreground">{label}</p><p className="text-sm font-medium">{value ?? '—'}</p></div>
);

export const SmartFareDetail = ({ o, currency, sessionId, open, onClose, onContinue }) => {
  useEffect(() => { if (open && o) api.trackSmartFare('smart_fare_detail_view', o.id, sessionId, { type: o.type }).catch(() => {}); }, [open, o, sessionId]);
  if (!o) return null;
  const c = o.comparison || {};
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="smart-fare-detail">
        <DialogHeader><DialogTitle className="flex items-center gap-1.5"><Sparkles className="h-5 w-5 text-accent" /> Smart Fare details</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Route" value={`${o.origin} → ${o.requested_destination}`} />
            <Field label="Airline" value={`${o.airline_name || ''} ${o.flight_number || ''}`} />
            <Field label="Smart fare" value={<span className="text-accent font-semibold">{formatPrice(o.smart_price, currency)}</span>} />
            <Field label="Default best fare" value={<span className={o.potential_saving > 0 ? 'line-through' : ''}>{formatPrice(o.normal_price, currency)}</span>} />
            {o.potential_saving > 0 && <Field label="You save" value={`${formatPrice(o.potential_saving, currency)} (${o.saving_percentage}%)`} />}
            <Field label="Smart Fare score" value={`${o.smart_fare_score}/100`} />
            <Field label="Stops" value={o.stops === 0 ? 'Non-stop' : `${o.stops} stop(s)`} />
            <Field label="Duration" value={fmtDur(o.duration_minutes)} />
            <Field label="Checked baggage" value={o.baggage_checked_kg ? `${o.baggage_checked_kg}kg` : 'Cabin only'} />
            <Field label="Booking agencies" value={o.agencies_count || '—'} />
          </div>
          <div className="rounded-xl bg-accent/5 border border-accent/20 p-3">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Info className="h-4 w-4 text-accent" /> Why this is a Smart Fare</p>
            <ul className="mt-1.5 space-y-1">
              {(o.reasons || []).map((r, i) => <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-accent shrink-0" />{r}</li>)}
            </ul>
          </div>
          {o.warning && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-400/40 p-3" data-testid="hidden-city-warning">
              <p className="text-sm font-semibold flex items-center gap-1.5 text-amber-700"><AlertTriangle className="h-4 w-4" /> How hidden-city works — read before booking</p>
              <p className="text-xs text-amber-800/90 mt-1">{o.warning}</p>
            </div>
          )}
          {(o.leg_segments || []).length > 0 && (
            <div className="rounded-xl border border-border bg-secondary/20 p-3" data-testid="hidden-city-legs">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">You fly {o.origin} → {o.exit_city_name || o.requested_destination} ({o.requested_destination}) — then exit</p>
              {(o.leg_segments || []).map((s, i) => (
                <p key={i} className="text-xs text-foreground/90">{s.from} → {s.to} · {s.airline_code} {s.flight_number}</p>
              ))}
              <p className="text-[11px] text-amber-700 mt-1">✗ Skip the onward leg {o.requested_destination} → {o.ticketed_final_city}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />vs default {fmtDur(c.baseline_duration_minutes)}</span>
            <span className="inline-flex items-center gap-1"><Luggage className="h-3.5 w-3.5" />default {c.baseline_baggage_checked_kg ? `${c.baseline_baggage_checked_kg}kg` : 'cabin only'}</span>
          </div>
          <Button onClick={() => { api.trackSmartFare('smart_fare_partner_click', o.id, sessionId, { type: o.type }).catch(() => {}); onContinue(o); }}
            className="w-full rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="smart-fare-continue">
            Book this fare <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">Live fare · booked on the partner site with your selected proposal</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
